import { log as debugLog } from '@/utils/logger'
import { toUtcSeconds } from '@/utils/utcTime'
import { mtfSuffix, tfSeconds } from '@/utils/mtf'
import BadgeComponent from './Badge.jsx'
import PanelComponent from './Panel.jsx'
import { BoxFillSeries } from '@/indicators/ict_quantum/BoxFillSeries'
import { LabelOverlaySeries } from '@/indicators/ict_quantum/LabelOverlaySeries'

// ─── Helpers de tiempo y validación ───────────────────────────────────────────

// Conversor ÚNICO compartido (@/utils/utcTime, v1.3.2): acepta segundos,
// milisegundos (payloads /charting/*), strings 'YYYY-MM-DD' (TFs diarios+) y
// business-day objects de LWC. Antes cleanTime solo convertía ms→s de números
// y los times string de 1D/1W/1M pasaban CRUDOS → comparaciones/Math.max
// número-vs-string en NaN o silencio (nada pintado en diario+). Contrato
// externo intacto: null para lo no interpretable, segundos UTC si no.
function cleanTime(t) {
  return toUtcSeconds(t)
}

function cleanValue(v) {
  if (v == null || Number.isNaN(v) || !Number.isFinite(v)) return null
  return v
}

function sortByTimeDesc(arr, key = 'time') {
  return [...arr].sort((a, b) => (cleanTime(b[key]) ?? 0) - (cleanTime(a[key]) ?? 0))
}

function normalizeDirection(dir) {
  const d = String(dir || '').toLowerCase()
  if (d === 'bull' || d === 'bisi' || d === 'long' || d === 'buy') return 'bull'
  if (d === 'bear' || d === 'sibi' || d === 'short' || d === 'sell') return 'bear'
  return null
}

function blockSizePercent(top, bottom) {
  const size = Math.abs(top - bottom)
  const mid = (Math.abs(top) + Math.abs(bottom)) / 2
  if (!mid || mid <= 0 || !Number.isFinite(size)) return 0
  return (size / mid) * 100
}

// ─── Datos externos (inyectados desde ChartPage / ChartPublicPage) ────────────

let externalData = null
let externalVersion = 0

export function setExternalData(data) {
  externalData = data
  externalVersion++
}

export function getExternalData() {
  return externalData
}

export function getExternalVersion() {
  return externalVersion
}

// ─── Configuración por defecto ──────────────────────────────────────────────────

export const defaultConfig = {
  _version: 4,

  // ── Visibilidad ──────────────────────────────────────────────────────────
  showBullOB: true,
  showBearOB: true,
  showLabels: true,

  // ── Lógica ─────────────────────────────────────────────────────────────────
  detectionMode: 'standard', // 'standard' | 'scalping'
  swingLength: 5,
  useBody: false,
  maxVisibleZones: 5, // Máx. bloques activos por lado (1–20)
  showMitigated: true, // Mostrar zonas mitigadas (atenuadas)
  showBreakers: false,
  chopControl: true,
  mitigation: 'wick', // 'close' | 'wick'
  htfTimeframes: [], // MTF: TFs adicionales activos (solo mayores que el del chart, máx. 2)

  // ── Visual / tamaño ────────────────────────────────────────────────────────
  minBlockSizePercent: 0, // 0 = mostrar todo
  boxLengthBars: 50,

  // ── Estilo ─────────────────────────────────────────────────────────────────
  fillOpacity: 0.14,
  borderWidth: 1.5,

  // ── Paleta Quantum ─────────────────────────────────────────────────────────
  colorBull: '#1B5E20',
  colorBear: '#B71C1C',

  // ── Dashboard ────────────────────────────────────────────────────────────
  showDashboard: true,
  dashboardMinimized: false,
  badgePosition: { x: 8, y: 8 },
  panelSections: {},
}

// ─── detect() ─────────────────────────────────────────────────────────────────

export function detect(candles, config) {
  const t0 = typeof performance !== 'undefined' ? performance.now() : 0
  if (!externalData) {
    const t1 = typeof performance !== 'undefined' ? performance.now() : 0
    debugLog(`[order_blocks_quantum] detect empty: ${(t1 - t0).toFixed(2)}ms`)
    return null
  }
  const result = {
    ...externalData,
    _dataVersion: externalVersion,
  }
  const t1 = typeof performance !== 'undefined' ? performance.now() : 0
  debugLog(`[order_blocks_quantum] detect from external: ${(t1 - t0).toFixed(2)}ms`)
  return result
}

// ─── render() ─────────────────────────────────────────────────────────────────

export function render(chart, candleSeries, result, config, api) {
  const t0 = typeof performance !== 'undefined' ? performance.now() : 0
  if (!result) return

  const cfg = { ...defaultConfig, ...(config || {}) }

  const allCandles = candleSeries && candleSeries.data ? candleSeries.data() : []
  // Ventana de DATOS cargada — fija e independiente del viewport. La detección
  // corre sobre ella (mismo from/to que el chart) y el viewport solo clipea
  // visualmente: una zona pintada jamás aparece/desaparece al hacer pan.
  // Reglas de reconciliación:
  // - Toda zona (activa, mitigada o breaker) se pinta si su intervalo
  //   intersecta la ventana cargada; si no, se descarta de datos y de badge.
  // - Origen anterior a la primera vela → borde izquierdo clipeado a ella.
  // - Activas: el backend devuelve end = última vela cargada (estructura
  //   viva — llega al presente) — se respeta tal cual.
  const firstCandleTime = allCandles.length > 0 ? cleanTime(allCandles[0].time) : null
  const lastCandleTime = allCandles.length > 0 ? cleanTime(allCandles[allCandles.length - 1].time) : null

  const labelsByTime = {}
  const addLabel = (opts) => {
    const t = opts?.time
    if (t == null || Number.isNaN(t)) return
    if (!labelsByTime[t]) labelsByTime[t] = []
    labelsByTime[t].push(opts)
  }

  const renderedZones = []
  const renderedLabels = []

  // ── Order Blocks ───────────────────────────────────────────────────────────
  const minSizePct = Math.max(0, Number(cfg.minBlockSizePercent) || 0)
  const showBreakers = cfg.showBreakers === true
  const showMitigated = cfg.showMitigated !== false

  const processObList = (obs, state) => {
    const out = []
    for (const ob of obs || []) {
      const start = cleanTime(ob.start_time)
      const top = cleanValue(ob.top)
      const bottom = cleanValue(ob.bottom)
      if (start == null || top == null || bottom == null) continue

      const direction = normalizeDirection(ob.direction)
      if (direction === 'bull' && !cfg.showBullOB) continue
      if (direction === 'bear' && !cfg.showBearOB) continue
      if (!direction) continue

      if (minSizePct > 0 && blockSizePercent(top, bottom) < minSizePct) continue

      const end = cleanTime(ob.end_time)
      if (end == null || end < start) continue

      const isBreaker = state === 'breaker' || ob.is_breaker === true
      const obState = isBreaker ? 'breaker' : state

      // Reconciliación con la ventana de datos (ver comentario superior):
      // intersecta o se descarta; borde izquierdo clipeado a la primera vela.
      if (firstCandleTime != null && end < firstCandleTime) continue
      if (lastCandleTime != null && start > lastCandleTime) continue
      const paintStart = firstCandleTime != null ? Math.max(start, firstCandleTime) : start
      const paintEnd = lastCandleTime != null ? Math.min(end, lastCandleTime) : end
      if (paintEnd < paintStart) continue

      // MTF: las zonas de timeframes adicionales vienen marcadas con `_mtf`
      // por el merge de la página (ver utils/mtf.js). Se distinguen por
      // etiqueta con sufijo de TF (`OB+ · 4H`) y borde dotted — tres texturas,
      // tres significados: sólido = TF propio, dashed = breaker, dotted = HTF.
      const mtf = ob._mtf && tfSeconds(ob._mtf) != null ? ob._mtf : null
      const color = direction === 'bull' ? cfg.colorBull : cfg.colorBear
      const label = (direction === 'bull' ? 'OB+' : 'OB−') + (mtf ? mtfSuffix(mtf) : '')

      let alpha
      let borderWidth
      let borderStyle
      if (obState === 'breaker') {
        alpha = Math.max(0.03, (cfg.fillOpacity || 0.14) * 0.45)
        borderWidth = cfg.borderWidth
        borderStyle = 'dashed'
      } else if (obState === 'mitigated') {
        // Atenuada pero legible sobre fondo claro (0.07 era invisible).
        alpha = Math.max(0.10, (cfg.fillOpacity || 0.14) * 0.75)
        borderWidth = 1.0
        borderStyle = 'solid'
      } else {
        alpha = cfg.fillOpacity
        borderWidth = cfg.borderWidth
        borderStyle = 'solid'
      }
      // HTF manda sobre la textura de estado: el usuario debe reconocer el TF
      // de un vistazo aunque la zona esté mitigada o sea breaker (el estado
      // sigue leyéndose en la opacidad).
      if (mtf) borderStyle = 'dotted'

      out.push({
        start: paintStart,
        end: paintEnd,
        top,
        bottom,
        color,
        alpha,
        borderWidth,
        borderStyle,
        label,
        type: 'OB',
        direction,
        state: obState,
        mtf,
        _sortTime: ob.start_time,
      })
    }
    return out
  }

  // Activas: limitadas POR LADO (maxVisibleZones bull + maxVisibleZones bear),
  // igual que el detector y como anuncia el panel ("Máx. bloques por lado").
  const activeObs = sortByTimeDesc(processObList(result.obs, 'active'), '_sortTime')
  const perSide = Math.max(0, cfg.maxVisibleZones ?? 5)
  const activeBySideObs = [
    ...activeObs.filter((z) => z.direction === 'bull').slice(0, perSide),
    ...activeObs.filter((z) => z.direction === 'bear').slice(0, perSide),
  ]
  const limitedActiveObs = activeBySideObs

  // Mitigadas: visibles si el toggle está ON, sin límite de activas
  const mitigatedObs = showMitigated
    ? sortByTimeDesc(processObList(result.mitigated_obs, 'mitigated'), '_sortTime')
    : []

  // Breakers: visibles si el toggle está ON, sin límite de activas
  const breakerObs = showBreakers
    ? sortByTimeDesc(processObList(result.breakers, 'breaker'), '_sortTime')
    : []

  // Orden de pintado: las zonas HTF primero (a TF mayor, más al fondo) y las
  // del TF propio encima — la estructura grande queda detrás y la del
  // timeframe activo manda visualmente.
  const allCandidates = sortByTimeDesc(
    [...limitedActiveObs, ...mitigatedObs, ...breakerObs],
    '_sortTime'
  )
  const htfCandidates = allCandidates
    .filter((z) => z.mtf)
    .sort((a, b) =>
      ((tfSeconds(b.mtf) ?? 0) - (tfSeconds(a.mtf) ?? 0)) ||
      ((cleanTime(b._sortTime) ?? 0) - (cleanTime(a._sortTime) ?? 0))
    )
  const ownCandidates = allCandidates.filter((z) => !z.mtf)
  const obCandidates = [...htfCandidates, ...ownCandidates]

  // ── Pintar zonas y etiquetas ───────────────────────────────────────────────
  const paintZone = (zone) => {
    const { start, end, top, bottom, color, alpha, borderWidth, borderStyle, label, type, direction, state, mtf } = zone

    const seriesData = [
      { time: start, upper: top, lower: bottom, fillColor: color, color, alpha, borderColor: color, borderWidth, borderStyle },
      { time: end, upper: top, lower: bottom, fillColor: color, color, alpha, borderColor: color, borderWidth, borderStyle },
    ]

    const boxSeries = api.addCustomSeries(new BoxFillSeries(), {
      color: 'rgba(0,0,0,0)',
      fillColor: 'rgba(0,0,0,0)',
      lineWidth: 0,
      lastValueVisible: false,
      priceLineVisible: false,
      borderWidth,
      borderStyle,
      alpha,
      autoscaleInfoProvider: () => null,
    })
    if (boxSeries) boxSeries.setData(seriesData)

    if (cfg.showLabels && label) {
      addLabel({
        time: start,
        price: top,
        text: label,
        color,
        textColor: color,
        anchor: 'above',
        offset: 6,
        priority: 5,
        plaqueStyle: 'plain',
      })
      renderedLabels.push({ time: start, price: top, text: label, type, direction, state, mtf })
    }

    renderedZones.push({ start, end, top, bottom, type, direction, color, state, alpha, borderStyle, mtf })
  }

  for (const z of obCandidates) paintZone(z)

  // Contadores de lo REALMENTE pintado — única fuente del badge, para que
  // badge y canvas cuadren 1:1 en cualquier config (toggles, min size…).
  // Los contadores clásicos solo cuentan el TF PROPIO (spec MTF: el badge no
  // cambia sus contadores); las zonas HTF van aparte, por TF, para la línea
  // "HTF: 4H ×3 · 1D ×1".
  const ownZones = renderedZones.filter((z) => !z.mtf)
  const htfCounts = {}
  for (const z of renderedZones) {
    if (z.mtf) htfCounts[z.mtf] = (htfCounts[z.mtf] || 0) + 1
  }
  const paintedCounts = {
    active_bull: ownZones.filter((z) => z.state === 'active' && z.direction === 'bull').length,
    active_bear: ownZones.filter((z) => z.state === 'active' && z.direction === 'bear').length,
    mitigated: ownZones.filter((z) => z.state === 'mitigated').length,
    breakers: ownZones.filter((z) => z.state === 'breaker').length,
    total: ownZones.length,
    htf: htfCounts,
  }
  // El objeto result viaja por referencia hasta indResults (setIndResults se
  // llama tras el render en el efecto del registro), así que el badge lo ve.
  result._paintedCounts = paintedCounts

  // ── Etiquetas de texto plano en canvas (una sola capa, frame a frame) ──────
  if (Object.keys(labelsByTime).length > 0) {
    const labelSeries = api.addCustomSeries(new LabelOverlaySeries(), {
      color: 'transparent',
      lineWidth: 0,
      lastValueVisible: false,
      priceLineVisible: false,
      crosshairMarkerVisible: false,
      autoscaleInfoProvider: () => null,
      priceScaleId: 'right',
      plaqueStyle: 'plain',
      font: '10px -apple-system, BlinkMacSystemFont, "Trebuchet MS", Roboto, Ubuntu, sans-serif',
    })
    if (labelSeries) {
      const labelData = Object.keys(labelsByTime)
        .map(t => ({ time: Number(t), labels: labelsByTime[t] }))
        .sort((a, b) => a.time - b.time)
      labelSeries.setData(labelData)
    }
  }

  const t1 = typeof performance !== 'undefined' ? performance.now() : 0
  debugLog(`[order_blocks_quantum] render done: ${(t1 - t0).toFixed(2)}ms`)

  // Exponer datos de render para verificación programática externa (Playwright).
  if (typeof window !== 'undefined') {
    window.__orderBlocksRender = {
      zones: renderedZones,
      labels: renderedLabels,
      counts: paintedCounts,
      timestamp: Date.now(),
    }
  }
}

export default {
  id: 'order_blocks_quantum',
  name: 'Order Blocks Quantum',
  version: '1.2.0',
  // Nada de este render depende del viewport: las primitivas son nativas del
  // chart y se desplazan con el pan. Opt-out del repintado por panTick para
  // que las zonas no parpadeen al arrastrar.
  renderOnPan: false,
  defaultConfig,
  detect,
  render,
  BadgeComponent,
  PanelComponent,
}
